import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ArrowUpRight,
  Compass,
  Anchor,
  Plant,
  GraduationCap,
  Buildings,
  Lightbulb,
  Shield,
  TrafficSign,
  Wheelchair,
  Leaf,
  Heartbeat,
  Handshake,
} from "@phosphor-icons/react";
import { Reveal, Stagger, fadeItem } from "@/components/site/Reveal";
import EventBanner from "@/components/site/EventBanner";
import { STATS, VERTICALS, EVENTS } from "@/lib/content";

const KOZHIKODE_SKETCH =
  "https://customer-assets.emergentagent.com/job_kozhikode-yi-connect/artifacts/dbkkprq3_ChatGPT%20Image%20May%2014%2C%202026%20at%2001_00_05%20PM.png";

const verticalIcons = {
  thalir: GraduationCap,
  yuva: Buildings,
  rural: Plant,
  masoom: Shield,
  "road-safety": TrafficSign,
  accessibility: Wheelchair,
  climate: Leaf,
  health: Heartbeat,
  entrepreneurship: Compass,
  leadership: Anchor,
  innovation: Lightbulb,
  csr: Handshake,
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const sketchOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.15]);

  return (
    <main data-testid="home-page" className="bg-parchment text-ocean">
      {/* PROMO BANNER — Next event */}
      <div className="pt-24 md:pt-28">
        <EventBanner />
      </div>

      {/* HERO */}
      <section ref={heroRef} className="relative overflow-hidden bg-parchment pt-8">
        {/* Tricolour ribbon under the event banner */}
        <div className="absolute top-0 left-0 right-0 h-px flex z-10">
          <div className="flex-1 bg-saffron/70" />
          <div className="flex-1 bg-parchment-deep" />
          <div className="flex-1 bg-leaf/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-6 lg:gap-12 items-center py-12 md:py-16 lg:py-20">
          {/* LEFT — text column */}
          <motion.div style={{ y }} className="md:col-span-6 order-2 md:order-1">
            <Reveal>
              <div className="overline text-ocean/60 mb-6 flex items-center gap-3">
                <span className="w-10 h-px bg-saffron" />
                Young Indians · Kozhikode Chapter
              </div>
            </Reveal>
            <Reveal delay={1}>
              <h1
                data-testid="hero-headline"
                className="font-serif text-ocean text-5xl sm:text-6xl md:text-6xl lg:text-7xl xl:text-8xl leading-[0.92] tracking-tight"
              >
                <span className="block text-saffron font-medium whitespace-nowrap">Heirs of the</span>
                <em className="block italic font-light text-leaf whitespace-nowrap">Spice Route.</em>
              </h1>
            </Reveal>
            <Reveal delay={2}>
              <p className="mt-8 max-w-xl text-ocean/75 text-base md:text-lg leading-relaxed">
                A thousand years ago, this coast set the price of pepper in Rome and the standard for
                honest trade across the Indian Ocean. We’re the next generation of stakeholders —
                and we’re still in business.
              </p>
            </Reveal>
            <Reveal delay={3}>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link
                  to="/initiatives"
                  data-testid="hero-cta-initiatives"
                  className="overline px-6 py-4 bg-ocean text-parchment hover:bg-saffron hover:text-ocean transition-colors flex items-center gap-2"
                >
                  Explore initiatives <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/heritage"
                  data-testid="hero-cta-heritage"
                  className="overline px-6 py-4 border border-ocean text-ocean hover:bg-leaf hover:border-leaf hover:text-parchment transition-colors flex items-center gap-2"
                >
                  The Calicut story
                </Link>
              </div>
            </Reveal>
          </motion.div>

          {/* RIGHT — Kozhikode sketch */}
          <motion.div
            style={{ opacity: sketchOpacity }}
            className="md:col-span-6 order-1 md:order-2 flex items-center justify-center md:justify-end"
          >
            <img
              src={KOZHIKODE_SKETCH}
              alt="Pencil sketch of Kozhikode — lighthouse, heritage building, palms and a dhow on the water"
              className="w-full max-w-[640px] h-auto md:scale-110 lg:scale-[1.18] md:translate-x-4 lg:translate-x-8 select-none"
              draggable={false}
              style={{
                mixBlendMode: "multiply",
                filter: "contrast(1.05)",
                maskImage:
                  "radial-gradient(ellipse 75% 80% at 55% 55%, #000 55%, rgba(0,0,0,0.7) 75%, transparent 100%)",
                WebkitMaskImage:
                  "radial-gradient(ellipse 75% 80% at 55% 55%, #000 55%, rgba(0,0,0,0.7) 75%, transparent 100%)",
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* INTRO STRIP */}
      <section className="py-20 md:py-32 border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline text-spice">01 · The Chapter</div>
            <div className="divider-brass mt-6" />
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <h2 className="font-serif-display text-4xl md:text-6xl">
                Kozhikode has always been a city that <em className="text-spice">moves things forward</em> —
                ships, ideas, people, futures.
              </h2>
            </Reveal>
            <Reveal delay={1}>
              <p className="mt-8 text-lg md:text-xl text-ocean/75 leading-relaxed max-w-2xl">
                Yi Kozhikode is the local chapter of Young Indians, an integral part of CII. We bring
                together founders, professionals and students to lead nation-building work — through
                schools, colleges, villages and the boardroom. Built on the soil that once anchored the
                world’s spice trade, we’re here to write what comes next.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="py-20 md:py-28 bg-parchment-deep border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
            <div>
              <div className="overline text-spice">02 · By the numbers</div>
              <h2 className="font-serif-display text-4xl md:text-5xl mt-4">The Yi footprint</h2>
            </div>
            <p className="text-ocean/60 max-w-sm text-sm">
              Numbers from across the national movement — Kozhikode is a proud node in this network.
            </p>
          </div>
          <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-px bg-black/10">
            {STATS.map((s, i) => (
              <motion.div
                key={i}
                variants={fadeItem}
                data-testid={`stat-card-${i}`}
                className="bg-parchment-deep p-8 md:p-10"
              >
                <div className="font-serif text-5xl md:text-6xl text-ocean">{s.value}</div>
                <div className="overline text-ocean/60 mt-3">{s.label}</div>
              </motion.div>
            ))}
          </Stagger>
        </div>
      </section>

      {/* VERTICALS PREVIEW (Bento) */}
      <section className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-5">
              <div className="overline text-spice">03 · What we do</div>
              <h2 className="font-serif-display text-5xl md:text-7xl mt-4">
                Twelve verticals.
                <br />
                <em className="text-spice">One coastline.</em>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7">
              <p className="text-ocean/75 text-lg leading-relaxed">
                From Thalir’s school assemblies to Yuva’s campus chapters, rural livelihoods to urban
                accessibility, our work is organised around three pillars: Nation Building, Youth Leadership
                and Thought Leadership.
              </p>
              <Link
                to="/initiatives"
                data-testid="home-cta-all-initiatives"
                className="inline-flex items-center gap-2 overline mt-8 text-ocean hover:text-spice"
              >
                Pillar deep-dive <ArrowUpRight size={16} weight="bold" />
              </Link>
            </div>
          </div>

          <Stagger className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {VERTICALS.map((v) => {
              const Icon = verticalIcons[v.slug] || Compass;
              return (
                <motion.div
                  key={v.slug}
                  variants={fadeItem}
                  data-testid={`vertical-card-${v.slug}`}
                  className="group relative border border-black/10 bg-parchment p-6 md:p-7 hover:border-spice transition-colors flex flex-col h-full"
                >
                  <div className="flex items-start justify-between">
                    <Icon size={26} weight="light" className="text-spice" />
                    <span className="overline text-ocean/40 text-[0.65rem]">{v.pillar}</span>
                  </div>
                  <div className="mt-8 flex-1">
                    <div className="overline text-spice">{v.tag}</div>
                    <h3 className="font-serif text-2xl md:text-3xl mt-2 leading-tight">{v.name}</h3>
                    <p className="mt-3 text-ocean/70 leading-relaxed text-sm">{v.desc}</p>
                  </div>
                </motion.div>
              );
            })}
          </Stagger>
        </div>
      </section>

      {/* EVENTS BAND */}
      <section className="py-20 md:py-32 bg-ocean text-parchment relative grain overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <div className="overline text-brass">04 · Calendar</div>
            <h2 className="font-serif-display text-5xl md:text-6xl mt-4">
              Signature
              <br />
              <em className="text-brass">moments.</em>
            </h2>
            <p className="mt-6 text-parchment/70 leading-relaxed">
              A year at Yi Kozhikode unfolds in flagship gatherings — each one designed to compound
              learning, friendships and impact.
            </p>
          </div>
          <div className="md:col-span-8">
            <ul>
              {EVENTS.map((e, i) => (
                <Reveal key={e.name} delay={i}>
                  <li
                    data-testid={`event-${i}`}
                    className="grid grid-cols-12 gap-6 py-8 border-t border-white/15 last:border-b items-center hover:bg-white/5 px-2 transition-colors"
                  >
                    <div className="col-span-1 font-serif text-2xl text-brass">{String(i + 1).padStart(2, "0")}</div>
                    <div className="col-span-11 md:col-span-4">
                      <div className="font-serif text-2xl md:text-3xl">{e.name}</div>
                    </div>
                    <div className="col-span-12 md:col-span-7 text-parchment/70 text-sm md:text-base leading-relaxed">
                      {e.blurb}
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="overline text-spice">Join us</div>
            <h2 className="font-serif-display text-5xl md:text-7xl mt-4 leading-[0.9]">
              The next chapter of Calicut is being written.
              <br />
              <em className="text-spice">Pull up a chair.</em>
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="home-cta-join"
              className="overline px-8 py-5 bg-ocean text-parchment hover:bg-spice transition-colors flex items-center gap-2"
            >
              Get in touch <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
